import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, CheckCircle2, type LucideIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import DemoCTA from "@/components/DemoCTA";
import ClientLogos from "@/components/ClientLogos";
import ROICalculator from "@/components/ROICalculator";
import { ApplicationBreadcrumb } from "@/components/ApplicationBreadcrumb";
import { Button } from "@/components/ui/button";
import { useSectionView } from "@/hooks/use-section-view";
import { track } from "@/lib/track";
import abstractBg from "@/assets/abstract-bg.webp";

export interface ApplicationPageConfig {
  /** Key under `applicationPages` and `applications` in the translation files */
  key: string;
  /** URL slug under /aplicacoes/ */
  slug: string;
  icon: LucideIcon;
  useCases: { key: string; icon: LucideIcon }[];
  benefits: string[];
  /** Value of the "Aplicação de Interesse" select in the demo form */
  formApplication: string;
}

const CASE_URL = "/case-study/eleeva-it";

const ApplicationPage = ({ config }: { config: ApplicationPageConfig }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [roiOpen, setRoiOpen] = useState(false);

  const p = (k: string) => t(`applicationPages.${config.key}.${k}`);
  const page = `app_${config.slug}`;
  const url = `https://aroan.com.br/aplicacoes/${config.slug}`;
  const appName = t(`applications.${config.key}.title`);
  const HeroIcon = config.icon;

  const useCasesRef = useSectionView<HTMLElement>("use_cases", page);
  const benefitsRef = useSectionView<HTMLElement>("benefits", page);
  const proofRef = useSectionView<HTMLElement>("proof", page);
  const finalRef = useSectionView<HTMLElement>("final_cta", page);

  const openRoi = () => {
    track("roi_open", { location: page });
    setRoiOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={p("pageTitle")}
        description={p("metaDescription")}
        canonical={url}
        ogType="website"
        article={false}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://aroan.com.br/" },
          { name: appName, url },
        ]}
      />
      <Header />

      <main>
        <section className="relative pt-24 pb-20 lg:pt-28 lg:pb-28 bg-gradient-hero overflow-hidden">
          <div
            className="absolute inset-0 opacity-40"
            style={{ backgroundImage: `url(${abstractBg})`, backgroundSize: "cover", backgroundPosition: "center" }}
          />
          <div className="container w-full min-w-0 mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ApplicationBreadcrumb applicationName={appName} className="mb-12" />
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-6">
                <HeroIcon className="w-8 h-8 text-primary" />
              </div>
              <p className="inline-block px-4 py-1.5 mb-6 rounded-full bg-accent text-sm font-medium text-foreground">
                {p("heroEyebrow")}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-foreground">
                {p("heroTitle")}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                {p("heroDescription")}
              </p>
              <DemoCTA location={`${page}_hero`} defaultApplication={config.formApplication} />
              <Button variant="ghost" className="mt-2" onClick={openRoi}>
                <Calculator className="mr-2 h-4 w-4" />
                {t("applicationPages.common.calculateRoi")}
              </Button>
            </div>
          </div>
        </section>

        <section ref={useCasesRef} className="py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-foreground">
                {t("applicationPages.common.useCases")}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{p("useCasesSubtitle")}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {config.useCases.map(({ key, icon: Icon }) => (
                <div
                  key={key}
                  className="bg-card border border-border rounded-2xl p-8 transition-all duration-300 hover:border-primary/20 hover:shadow-card"
                >
                  <div className="w-14 h-14 rounded-xl bg-accent flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">{p(`useCases.${key}.title`)}</h3>
                  <p className="text-muted-foreground text-lg">{p(`useCases.${key}.description`)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section ref={benefitsRef} className="py-20 lg:py-32 bg-accent/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-12 text-foreground text-center">
                {t("applicationPages.common.benefits")}
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {config.benefits.map((key) => (
                  <div key={key} className="flex gap-4 bg-card border border-border rounded-2xl p-6">
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-foreground">{p(`benefits.${key}.title`)}</h3>
                      <p className="text-muted-foreground">{p(`benefits.${key}.description`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section ref={proofRef} className="py-20 lg:py-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-foreground">
                {t("applicationPages.common.proofTitle")}
              </h2>
              <p className="text-lg text-muted-foreground mb-6">{t("applicationPages.common.proofText")}</p>
              <Button
                variant="outline"
                className="group"
                onClick={() => {
                  track("case_click", { location: page });
                  navigate(CASE_URL);
                }}
              >
                {t("results.readCase")}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <ClientLogos />
          </div>
        </section>

        <section ref={finalRef} className="py-20 lg:py-32 bg-accent/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-foreground">{p("ctaTitle")}</h2>
              <p className="text-xl text-muted-foreground mb-4">{p("ctaDescription")}</p>
              <p className="text-base font-semibold mb-8">{t("ctaMultiple.price")}</p>
              <DemoCTA location={`${page}_final_cta`} defaultApplication={config.formApplication} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ROICalculator open={roiOpen} onOpenChange={setRoiOpen} />
    </div>
  );
};

export default ApplicationPage;
